import React, { useEffect, useState } from 'react';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import {
  DynamicFormInputType,
  IDynamicSection,
  IFormDateDynamicProps,
  IFormSelectDynamicProps,
} from '@components/_old/dynamicForm';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { inputs } from './content';
import { useMutation, useQuery } from '@apollo/client';
import Loader from '@components/loader/Loader';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import { IExpatPolicyDetailsDrawerProps } from '.';
import SectionDynamicForm from '@components/_old/dynamicForm/SectionDynamicForm';
import { getListForm, updateExpatPolicy } from './queries';
import { toLookups } from './utils';
import { getError } from '@utils/graph-utils';
import { normaliseDynamicValues } from '@utils/dynamic-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useAppSelector } from '@redux/hooks';
import DateService from '@services/DateService';

const ExpatPolicyDetailsDrawer: React.FC<IExpatPolicyDetailsDrawerProps> = ({
  policyId,
  open,
  onSuccess,
  onClose,
  policyDetailsInfo,
}) => {
  const user = useAppSelector((state) => state.user);
  const isAdmin = user.userRoles.includes('Insurance-Admin');

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] =
    useState<Record<string, DynamicFormInputType>>(inputs);

  const policyDetailsListResults = useQuery(getListForm());

  const [policyAction] = useMutation(updateExpatPolicy());

  const sections: Record<string, IDynamicSection> = {
    section1: {
      title: 'Policy Details',
      inputs: {
        policyNumber: inputsForm.policyNumber,
        isRenewal: inputsForm.isRenewal,
        renewalNumber: inputsForm.renewalNumber,
        policyEffectiveDate: inputsForm.policyEffectiveDate,
        createdOn: inputsForm.createdOn,
        policyExpiryDate: inputsForm.policyExpiryDate,
        product: inputsForm.product,
      },
    },
    section2: {
      specificTitleDesign: () => (
        <>
          {`Subscriber's Details `}
          <span
            style={{
              fontStyle: 'italic',
              fontSize: `${MAIN_THEME.fontSize.sm}`,
            }}
          >
            (Please enter all details in Arabic)
          </span>
        </>
      ),
      hasTitleSpecificDesign: true,
      inputs: {
        title: inputsForm.title,
        firstName: inputsForm.firstName,
        middleName: inputsForm.middleName,
        lastName: inputsForm.lastName,
        email: inputsForm.email,
        introducedBy: inputsForm.introducedBy,
        phoneType: inputsForm.phoneType,
        mobileNumber: inputsForm.mobileNumber,
        country: inputsForm.country,
        city: inputsForm.city,
        street: inputsForm.street,
        building: inputsForm.building,
      },
    },
    section3: {
      title: 'Insured Details',
      inputs: {
        insuredFirstName: inputsForm.insuredFirstName,
        insuredMiddleName: inputsForm.insuredMiddleName,
        insuredLastName: inputsForm.insuredLastName,
        insuredDateOfBirth: inputsForm.insuredDateOfBirth,
        insuredNationality: inputsForm.insuredNationality,
        insuredGender: inputsForm.insuredGender,
        insuredPlan: inputsForm.insuredPlan,
        insuredPassportNumber: inputsForm.insuredPassportNumber,
        insuredOccupation: inputsForm.insuredOccupation,
      },
    },
  };

  const userAgentType = user.info.agentType;

  useEffect(() => {
    initialize();
  }, [policyDetailsListResults]);

  const initialize = () => {
    try {
      const updatedInputs = cloneDeep(inputsForm);

      const agentType = (userAgentType ?? '').trim().toUpperCase();
      const isInternal = agentType === 'INTERNAL';
      const isExternal = agentType === 'EXTERNAL';

      // ---- Lookups ----
      if (policyDetailsListResults.data) {
        const lookupLists = toLookups(
          policyDetailsListResults.data,
          userAgentType
        );

        (updatedInputs.phoneType as IFormSelectDynamicProps).selectOptions =
          lookupLists['phoneType'];
        (updatedInputs.country as IFormSelectDynamicProps).selectOptions =
          lookupLists['countries'];
        (updatedInputs.isRenewal as IFormSelectDynamicProps).selectOptions =
          lookupLists['renewalStatus'];
        (
          updatedInputs.insuredNationality as IFormSelectDynamicProps
        ).selectOptions = lookupLists['nationalities'];
        (updatedInputs.title as IFormSelectDynamicProps).selectOptions =
          lookupLists['title'];
        (updatedInputs.insuredGender as IFormSelectDynamicProps).selectOptions =
          lookupLists['insuredGender'];
        (updatedInputs.insuredPlan as IFormSelectDynamicProps).selectOptions =
          lookupLists['insuredPlan'];
        (
          updatedInputs.insuredOccupation as IFormSelectDynamicProps
        ).selectOptions = lookupLists['occupations'];
      }

      updatedInputs.insuredGender.disabled = !isInternal;
      updatedInputs.insuredNationality.disabled = !isInternal;
      updatedInputs.insuredDateOfBirth.disabled = !isInternal;

      updatedInputs.insuredPlan.disabled =
        !isInternal ||
        policyDetailsInfo?.insuredGenderCode?.toUpperCase() !== 'MALE' ||
        policyDetailsInfo?.insuredNationalityId?.toString() !== '290' ||
        !isAgeInRange(policyDetailsInfo?.insuredDateOfBirth);

      updatedInputs.insuredPlan.hidden = updatedInputs.insuredPlan.disabled;

      updatedInputs.insuredPlan.required =
        !(
          !isInternal ||
          policyDetailsInfo?.insuredGenderCode?.toUpperCase() !== 'MALE' ||
          policyDetailsInfo?.insuredNationalityId?.toString() !== '290'
        ) || !isAgeInRange(policyDetailsInfo?.insuredDateOfBirth);

      (updatedInputs.policyEffectiveDate as IFormDateDynamicProps).onChange = (
        event
      ) => {
        if (event) {
          const picked = new Date(event);
          setInputsForm((current) => ({
            ...current,
            policyExpiryDate: {
              ...current.policyExpiryDate,
              value: DateService.addYears(picked, 1)?.toISOString(),
            },
          }));
        } else {
          setInputsForm((current) => ({
            ...current,
            policyExpiryDate: { ...current.policyExpiryDate, value: '' },
          }));
        }
      };

      // ---- Populate from API ----
      if (policyDetailsInfo) {
        updatedInputs.policyEffectiveDate.value =
          policyDetailsInfo.policyEffectiveDate;
        updatedInputs.createdOn.value = policyDetailsInfo.createdOn;
        updatedInputs.policyExpiryDate.value =
          policyDetailsInfo.policyExpiryDate;
        updatedInputs.product.value = policyDetailsInfo.plan;
        updatedInputs.productCode.value = policyDetailsInfo.productCode;

        updatedInputs.title.value = policyDetailsInfo.title;
        updatedInputs.firstName.value = policyDetailsInfo.firstName;
        updatedInputs.middleName.value = policyDetailsInfo.middleName;
        updatedInputs.lastName.value = policyDetailsInfo.lastName;
        updatedInputs.email.value = policyDetailsInfo.email;
        updatedInputs.phoneType.value = policyDetailsInfo.phoneType;
        updatedInputs.mobileNumber.value = policyDetailsInfo.mobileNumber;
        updatedInputs.country.value = policyDetailsInfo.countryId;
        updatedInputs.city.value = policyDetailsInfo.city;
        updatedInputs.street.value = policyDetailsInfo.street;
        updatedInputs.building.value = policyDetailsInfo.building;

        updatedInputs.isRenewal.value = policyDetailsInfo.isRenewal;
        updatedInputs.renewalNumber.value = policyDetailsInfo.renewalNumber;
        updatedInputs.renewalNumber.hidden =
          policyDetailsInfo.isRenewal !== 'YES';
        updatedInputs.renewalNumber.required =
          policyDetailsInfo.isRenewal === 'YES';

        updatedInputs.policyNumber.value = policyDetailsInfo.fullPolicyNumber;

        updatedInputs.insuredPlan.value = policyDetailsInfo.insuredPlan;
        updatedInputs.insuredFirstName.value =
          policyDetailsInfo.insuredFirstName;
        updatedInputs.introducedBy.value = policyDetailsInfo.introducedBy;
        updatedInputs.insuredMiddleName.value =
          policyDetailsInfo.insuredMiddleName;
        updatedInputs.insuredLastName.value = policyDetailsInfo.insuredLastName;
        updatedInputs.insuredDateOfBirth.value =
          policyDetailsInfo.insuredDateOfBirth;
        updatedInputs.insuredNationality.value =
          policyDetailsInfo.insuredNationalityId;
        updatedInputs.insuredGender.value = policyDetailsInfo.insuredGenderCode;
        updatedInputs.insuredPassportNumber.value =
          policyDetailsInfo.insuredPassportNumber;
        updatedInputs.insuredOccupation.value =
          policyDetailsInfo.insuredOccupationId;
      }

      // ---- LOVs (PolicyEffectiveDate) per AC ----
      {
        const effInput =
          updatedInputs.policyEffectiveDate as IFormDateDynamicProps;
        const issueDateSrc =
          policyDetailsInfo?.createdOn ??
          (updatedInputs.createdOn?.value as any) ??
          null;

        if (issueDateSrc) {
          const base = new Date(issueDateSrc);
          if (!isNaN(base.getTime())) {
            const yearStart = new Date(base.getFullYear(), 0, 1);
            const yearEnd = new Date(
              base.getFullYear(),
              11,
              31,
              23,
              59,
              59,
              999
            );

            const now = new Date();
            const todayStart = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate()
            );

            // Base ranges
            const baseMin = isExternal
              ? base
              : DateService.subtractMonths(base, 3);
            const baseMax = DateService.addMonths(base, 1);

            // Final ranges (floored at Today)
            const rawMin = isExternal
              ? todayStart
              : DateService.subtractMonths(todayStart, 3);
            const rawMax = baseMax;

            const finalMin = rawMin < yearStart ? yearStart : rawMin;
            const finalMax = rawMax > yearEnd ? yearEnd : rawMax;

            effInput.minDate = finalMin > finalMax ? undefined : finalMin;
            effInput.maxDate = finalMin > finalMax ? undefined : finalMax;
          }
        }
      }

      // ---- Disable unless Today ∈ [Issue, Issue+1M] ----
      {
        const effInput =
          updatedInputs.policyEffectiveDate as IFormDateDynamicProps;

        const issueDateSrc =
          policyDetailsInfo?.createdOn ??
          (updatedInputs.createdOn?.value as any) ??
          null;

        const issueDate = issueDateSrc ? new Date(issueDateSrc) : undefined;

        if (!issueDate || isNaN(issueDate.getTime())) {
          effInput.disabled = true;
        } else {
          const windowEnd = DateService.addMonths(issueDate, 1);
          const start = new Date(
            issueDate.getFullYear(),
            issueDate.getMonth(),
            issueDate.getDate()
          );
          const end = new Date(
            windowEnd.getFullYear(),
            windowEnd.getMonth(),
            windowEnd.getDate(),
            23,
            59,
            59,
            999
          );
          const today = new Date();

          effInput.disabled = !(today >= start && today <= end);
        }
      }

      setInputsForm(updatedInputs);
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normaliseDynamicValues(inputs, values);

    if (data.isRenewal == 'NO') {
      data.renewalNumber = null;
    }

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variablesMutation = {
        entityId: policyId,
        insuredInputs: {
          insuredFirstName: data.insuredFirstName,
          insuredMiddleName: data.insuredMiddleName || null,
          insuredLastName: data.insuredLastName,
          insuredDateOfBirth: DateService.formatDateBackend(
            data.insuredDateOfBirth
          ),
          insuredNationality: data.insuredNationality,
          insuredGender: data.insuredGender,
          insuredPassportNumber: data.insuredPassportNumber,
          insuredOccupation: data.insuredOccupation,
          insuredPlan: data.insuredPlan || null,
          introducedBy: data?.introducedBy || null,
        },
        isRenewal: data.isRenewal,
        renewalNumber: data.renewalNumber,
        applicationContactInputs: {
          title: data.title,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          email: data.email || null,
          phoneType: data.phoneType,
          mobileNumber:
            data.mobileNumber && data.mobileNumber.length > 3
              ? data.mobileNumber.charAt(0) == '+'
                ? data.mobileNumber
                : '+' + data.mobileNumber
              : undefined,
          country: data.country,
          city: data.city,
          street: data.street || null,
          building: data.building || null,
        },
        effectiveDate: DateService.formatDateBackend(data.policyEffectiveDate),
      };
      policyAction({
        variables: variablesMutation,
        errorPolicy: 'all',
      }).then((res) => {
        if (isEmpty(res.errors)) {
          toast.success(
            <ToastSuccessMessage>
              {'Policy Updated Successfully'}
            </ToastSuccessMessage>
          );
          setTimeout(() => {
            setSubmitButtonState('success');
            onSuccess();
            onClose();
            setFormDisabled(false);
          }, 500);
        } else {
          setSubmitButtonState(undefined);
          setFormDisabled(false);
          toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        }
      });
    } catch {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      //
    }
  };

  return (
    <GenericDrawer
      title="Edit Policy Details"
      onClose={() => onClose()}
      isOpen={open}
    >
      {policyDetailsListResults.loading && open ? (
        <Loader />
      ) : (
        <>
          <SectionDynamicForm
            onSubmit={(values) => submitForm(values)}
            buttonText={'Submit'}
            submitButtonState={submitButtonState}
            disableForm={formDisabled}
            inputs={inputsForm}
            sections={sections}
            onChange={(
              fieldName: string,
              value: string | string[],
              allValues
            ) => {
              const formInputs = cloneDeep(inputsForm);

              if (fieldName === 'isRenewal') {
                if (value == 'YES') {
                  formInputs.renewalNumber.required = true;
                  formInputs.renewalNumber.hidden = false;
                } else {
                  formInputs.renewalNumber.hidden = true;
                  formInputs.renewalNumber.required = false;
                }

                Object.values(formInputs).forEach((input) => {
                  input.value = allValues[input.name];
                });

                setInputsForm({ ...formInputs });
              }

              if (userAgentType == 'INTERNAL') {
                if (
                  fieldName === 'insuredGender' ||
                  fieldName === 'insuredNationality' ||
                  fieldName === 'insuredDateOfBirth'
                ) {
                  formInputs[fieldName].value = value;
                  if (
                    allValues?.insuredGender?.toUpperCase() !== 'MALE' ||
                    allValues?.insuredNationality?.toString() !== '290' ||
                    !isAgeInRange(allValues?.insuredDateOfBirth)
                  ) {
                    formInputs.insuredPlan.hidden = true;
                    formInputs.insuredPlan.disabled = true;
                    formInputs.insuredPlan.required = false;
                    formInputs.insuredPlan.value = undefined;
                  } else {
                    formInputs.insuredPlan.hidden = false;
                    formInputs.insuredPlan.disabled = false;
                    formInputs.insuredPlan.required = true;
                  }
                  setInputsForm({ ...formInputs });
                }
              }
            }}
          />
        </>
      )}
    </GenericDrawer>
  );
};

export default ExpatPolicyDetailsDrawer;

function isAgeInRange(insuredDOB: Date | string): boolean {
  const actualAge = DateService.getAge(insuredDOB);

  // Check if age is within the range [18, 55]
  return actualAge >= 18 && actualAge <= 55;
}
